import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { RANGE_LABEL, type RangeKey, type TopologyEdge, type TopologyNode, type TopologyNodePosition } from "@k8s-dashboard/contracts";
import { topoKeys, useEdgeSeries, useSaveTopologyLayout, useTopology } from "@/api/queries";
import { useDashboardParams } from "@/state/useDashboardParams";
import { LineChart } from "@/components/LineChart";
import { Panel, StatTile, StatusBadge } from "@/components/primitives";
import { SectionView } from "@/components/SectionState";
import { Breadcrumb, LogsLink, drillCrumbs, refPath } from "@/components/drill";
import { duration, num, since } from "@/lib/format";
import { PageError, PageHeader, useDrillControls, useInvalidate } from "@/features/drill/common";
import { useLogSearch } from "@/api/queries";
import { useT } from "@/i18n/I18nProvider";
import { TopologyGraph } from "./TopologyGraph";

/**
 * Route를 클릭하면 그 통신 경로의 **출발 Pod 실제 최근 로그**를 조회해 보여줍니다. (#31 후속)
 * 실시간 패킷 캡처는 이 조회 전용 대시보드가 해선 안 되는 일이라(특권 프로세스·PII·TLS),
 * 이미 수집 중인 로그를 **실제 Pod UID**로 필터해 "이 경로에서 실제로 오간 것"에 가장
 * 가까운 실데이터를 보여줍니다. 서버 마스킹이 그대로 적용됩니다(README §10).
 *
 * route 문자열 자체(예: /api/v1/orders)는 통신 그래프가 아직 demo라 실제 로그와
 * 매칭되지 않으므로 검색어가 아니라 보조 필터로만 씁니다.
 */
function RouteLogs({
  clusterId,
  fromNode,
  route,
  range,
}: {
  clusterId: string;
  fromNode: TopologyNode | undefined;
  route: string;
  range: RangeKey;
}) {
  const external = fromNode?.external ?? false;
  const q = useLogSearch({
    clusterId,
    namespace: fromNode?.namespace || "all",
    workload: "",
    podUid: fromNode?.ref.podUid ?? "",
    container: "",
    levels: [],
    q: "",
    range,
  });
  const section = q.data?.pages[0]?.lines;
  const lines = (section?.status === "ok" ? section.data : [])?.slice(0, 8) ?? [];
  const ref = q.data?.pages[0]?.generatedAt ?? new Date().toISOString();

  if (external || !fromNode?.ref.podUid) {
    return <div className="topo-route-logs__hint muted">외부 엔티티({fromNode?.name})는 클러스터 로그가 없습니다. 내부 Pod가 출발인 경로를 선택하세요.</div>;
  }
  if (q.isLoading) return <div className="topo-route-logs__hint muted">{fromNode.name}의 최근 로그를 불러오는 중…</div>;
  if (section && section.status !== "ok") {
    return <div className="topo-route-logs__hint muted">로그를 조회할 수 없습니다({section.status}). 로그 데이터소스 연결을 확인하세요.</div>;
  }
  if (lines.length === 0) {
    return <div className="topo-route-logs__hint muted">선택한 범위에 {fromNode.name}의 로그가 없습니다.</div>;
  }
  return (
    <div className="topo-route-logs">
      <div className="topo-route-logs__meta muted">
        <strong>{fromNode.name}</strong>의 최근 로그 (route <code>{route}</code>) · Quickwit 실데이터 · 민감정보는 서버에서 마스킹됨
      </div>
      <ul className="topo-route-logs__list">
        {lines.map((l) => (
          <li key={l.id}>
            <StatusBadge severity={l.level === "ERROR" ? "critical" : l.level === "WARN" ? "warning" : "healthy"} label={l.level} small />
            <span className="topo-route-logs__pod ds-ident">{l.podName}</span>
            <span className="topo-route-logs__msg" title={l.message}>{l.message}</span>
            <span className="topo-route-logs__time num muted">{since(new Date(l.t).toISOString(), ref)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * 구조 엣지의 **도달 경로 표**입니다. (ADR 0040)
 *
 * 구조 엣지 하나가 `진입점 → Service`와 `Service → 워크로드` 두 홉을 덮으므로
 * 포트가 둘입니다 — 한 칸으로 합치면 운영자가 어느 홉의 수인지 알 수 없습니다.
 *
 * 호스트·경로·포트는 **텍스트**입니다. 색은 상태, 두께는 양이라는 규칙
 * (`design-system/README.md` §3.4)을 여기서 어기지 않습니다. 상태 색을 쓰는 곳은
 * Ready 요약뿐이고, 거기서도 아이콘 + 텍스트 라벨과 함께 씁니다.
 */
function StructuralPaths({ edge }: { edge: TopologyEdge }) {
  const paths = edge.paths ?? [];
  const omitted = edge.pathsOmitted ?? 0;
  if (paths.length === 0) {
    return (
      <div className="topo-paths__hint muted" data-testid="topology-paths-empty">
        이 선의 경로를 색인이 채우지 못했습니다. 진입점(Ingress·HTTPRoute·Route)이 호스트·경로를 선언하지 않았거나,
        진입점 CRD가 이 배포에서 opt-in되어 있지 않습니다.
      </div>
    );
  }
  return (
    <div className="panel__scroll" data-testid="topology-paths">
      <table className="ds-data-table ds-data-table--compact">
        <thead>
          <tr>
            <th>Service</th>
            <th>호스트</th>
            <th>경로</th>
            <th style={{ width: 110 }}>서비스 포트</th>
            <th style={{ width: 130 }}>엔드포인트 포트</th>
            <th style={{ width: 210 }}>엔드포인트</th>
          </tr>
        </thead>
        <tbody>
          {paths.map((p, i) => (
            <tr key={`${p.service}-${p.host ?? ""}-${p.path ?? ""}-${p.servicePort ?? ""}-${i}`}>
              <td className="ds-ident">{p.service}</td>
              {/* 선언이 없는 것과 "전부"는 다른 사실입니다. 빈 칸으로 뭉개지 않습니다. */}
              <td className="ds-ident">{p.host || <span className="muted">호스트 제한 없음</span>}</td>
              <td className="ds-ident">
                {p.path ? (
                  <>
                    {p.path}
                    {p.pathMatch ? <span className="muted"> · {p.pathMatch}</span> : null}
                  </>
                ) : (
                  <span className="muted">경로 제한 없음</span>
                )}
              </td>
              <td className="ds-ident">{p.servicePort || <span className="muted">지정 없음</span>}</td>
              <td className="ds-ident">
                {p.endpointPortUnrestricted ? (
                  <>
                    {p.endpointPort ? `${p.endpointPort} · ` : ""}
                    <span className="muted">포트 제한 없음</span>
                  </>
                ) : p.endpointPort ? (
                  <>
                    {p.endpointPort}
                    {p.protocol ? <span className="muted"> · {p.protocol}</span> : null}
                  </>
                ) : (p.endpointPorts ?? 0) > 1 ? (
                  <span className="muted">포트 {num(p.endpointPorts ?? 0)}개</span>
                ) : (
                  <span className="muted">선언 없음</span>
                )}
              </td>
              <td className="topo-paths__ready">
                {/* 상태 색은 예약어입니다 — 언제나 아이콘 + 텍스트 라벨과 함께 씁니다. */}
                <StatusBadge severity="healthy" label={`Ready ${num(p.ready)}`} small />
                {p.notReady > 0 && <StatusBadge severity="critical" label={`Not Ready ${num(p.notReady)}`} small />}
                {/* "모름"은 "정상"이 아닙니다. 정상 색으로 칠하지 않습니다. (ADR 0040 결정 6) */}
                {p.unknown > 0 && <StatusBadge severity="unknown" label={`모름 ${num(p.unknown)}`} small />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {omitted > 0 && (
        <p className="topo-paths__omitted" role="status" data-testid="topology-paths-omitted">
          상한을 넘어 생략한 경로 {num(omitted)}개 — 이 선이 지나는 경로가 더 있습니다.
        </p>
      )}
    </div>
  );
}

/**
 * Pod Topology 화면
 * --------------------------------------------------------------------------
 * design-system의 topology preview를 앱에 연결한 화면입니다.
 * preview와 달라진 점 하나 — 비정상 Pod를 클릭하면 로그 모달이 아니라
 * **Pod 상세 화면**으로 갑니다. 이제 진짜 상세 화면과 Logs Explorer가 있으므로
 * 축약된 모달을 따로 유지할 이유가 없습니다.
 */
export function TopologyView() {
  const { t } = useT("common");
  const { search } = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { clusterId, namespace, range, from, to, refreshMs } = useDashboardParams();
  const q = useTopology(clusterId, namespace, range, refreshMs, from, to);
  const invalidate = useInvalidate(topoKeys.graph(clusterId, namespace, range));
  const { controls } = useDrillControls(invalidate, q.isFetching, q.dataUpdatedAt || undefined);

  const graph = q.data?.graph.data;
  const selectedEdgeId = params.get("edge") ?? graph?.edges[0]?.id ?? null;
  const edge = graph?.edges.find((e) => e.id === selectedEdgeId) ?? null;
  const [mode, setMode] = useState<"table" | "chart">("table");
  /* 구조 엣지는 "부를 수 있다"이고 실측은 "불렀다"입니다. 구조 엣지에는 누적 수도
     시계열도 없으므로(도달 가능성은 시간에 따라 변하는 양이 아닙니다) 실측 표
     대신 도달 경로 표를 그리고, 시계열도 조회하지 않습니다. (ADR 0037 결정 4 · ADR 0040) */
  const structural = edge?.origin === "structure";
  const series = useEdgeSeries(clusterId, mode === "chart" && !structural ? selectedEdgeId : null, range, from, to);

  /* 공유 배치 편집 (#28) — Edit 모드는 조회 상태가 아니라 도구 상태이므로 URL이 아닌
     로컬 state에 둡니다. 드래그 좌표는 렌더와 무관하므로 ref로 받습니다. */
  /* 연결선이 없는 워크로드를 펼쳤는지. 화면 로컬 상태입니다 — 자동 갱신이 지우지
     않고, 링크로 공유할 값도 아닙니다. (ADR 0037 결정 9) */
  const [showIsolated, setShowIsolated] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editSession, setEditSession] = useState(0);
  const pendingPositions = useRef<TopologyNodePosition[] | null>(null);
  const saveLayout = useSaveTopologyLayout(clusterId);
  const canEdit = q.data?.canEditLayout ?? false;

  const exitEdit = () => {
    setEditMode(false);
    setEditSession((v) => v + 1); // 편집 중 좌표를 버리고 저장/기본 배치로 되돌립니다.
    pendingPositions.current = null;
  };
  const save = () => {
    const positions = pendingPositions.current;
    if (!positions) return setEditMode(false);
    saveLayout.mutate(positions, {
      onSuccess: () => {
        setEditMode(false);
        pendingPositions.current = null;
      },
    });
  };
  const resetLayout = () => {
    saveLayout.mutate([], { onSuccess: () => exitEdit() });
  };

  /* 저장된 배치가 이 화면에 하나도 적용되지 않았는지. 서버가 같은 규칙으로 센 값을
     그대로 읽습니다 — 그래프 섹션이 ok일 때만 유효하므로 조건에 status를 함께 둡니다.
     upstream 장애를 "낡은 배치"로 바꿔 말하지 않기 위해서입니다. (#123) */
  const savedPositionCount = q.data?.layout?.positions.length ?? 0;
  const layoutStale = savedPositionCount > 0 && (q.data?.layoutApplied ?? 0) === 0 && q.data?.graph.status === "ok";
  /* 저장소를 읽지 못한 것은 "저장 없음"과 다른 사실입니다. 같은 빈 화면으로 처리하지 않습니다.
     다만 서버는 읽기에 실패해도 같은 프로세스의 메모리 사본을 함께 실어 보낼 수 있고, 그때
     화면은 그 좌표로 그립니다 — "기본 배치로 그렸습니다"가 거짓이 되므로 실린 좌표가
     하나도 없을 때만 밝힙니다. (#123) */
  const layoutUnavailable = (q.data?.layoutUnavailable ?? false) && savedPositionCount === 0;

  const nodeName = (id?: string) => graph?.nodes.find((n) => n.id === id)?.name ?? id ?? "";
  const reverse = edge && graph?.edges.some((e) => e.from === edge.to && e.to === edge.from);
  const pods = q.data?.pods.data;

  /* Route 행을 열면 payload 표본(hex)이 펼쳐집니다. 선택한 선이 바뀌면 접습니다. (#31) */
  const [openRoute, setOpenRoute] = useState<string | null>(null);
  useEffect(() => setOpenRoute(null), [selectedEdgeId]);

  return (
    <div className="page">
      <PageHeader
        crumbs={
          <Breadcrumb
            items={drillCrumbs(search, undefined, { label: "Pod Topology" })}
          />
        }
        title="Pod Topology"
        subtitle={`${clusterId} · ${namespace === "all" ? "모든 Namespace" : namespace} · ${RANGE_LABEL[range]}`}
        controls={controls}
      />

      {q.isError ? (
        <PageError error={q.error} onRetry={invalidate} />
      ) : (
        <>
          {pods && (
            <div className="grid grid--kpi">
              <StatTile label="Pods" value={num(pods.total)} footnote="선택한 scope의 전체 Pod · 그래프는 워크로드 단위로 접어 표시" />
              <StatTile label="정상" value={num(pods.healthy)} footnote={`전체 ${num(pods.total)}`} />
              <StatTile
                label="비정상"
                value={num(pods.unhealthy)}
                tone={pods.unhealthy > 0 ? "critical" : undefined}
                footnote={pods.unhealthy > 0 ? "아래 목록에서 이름을 클릭하면 상세로 이동합니다" : "이상 없음"}
              />
            </div>
          )}

          <Panel
            title="통신 그래프"
            subtitle={
              editMode
                ? "편집 모드 — 노드를 드래그해 배치를 바꾼 뒤 저장하세요. 저장된 배치는 모든 사용자에게 적용됩니다."
                : "선을 클릭하면 방향별 상세가 열립니다 · A→B와 B→A는 방향별 별도 선입니다"
            }
            section={q.data?.graph}
            referenceIso={q.data?.generatedAt}
            actions={
              canEdit && (
                <span className="topo-toolbar">
                  {editMode ? (
                    <>
                      {saveLayout.isError && <span className="topo-toolbar__hint" role="alert">저장 실패 — 다시 시도하세요</span>}
                      <button type="button" className="linkish" onClick={resetLayout} disabled={saveLayout.isPending}>
                        기본 배치로
                      </button>
                      <button type="button" className="linkish" onClick={exitEdit} disabled={saveLayout.isPending}>
                        취소
                      </button>
                      <button type="button" className="ds-button ds-button--primary ds-button--sm" onClick={save} disabled={saveLayout.isPending}>
                        {saveLayout.isPending ? "저장 중…" : "배치 저장"}
                      </button>
                    </>
                  ) : (
                    <button type="button" className="ds-button ds-button--outline ds-button--sm" onClick={() => setEditMode(true)}>
                      배치 편집
                    </button>
                  )}
                </span>
              )
            }
          >
            {layoutUnavailable && (
              <p className="topo-layout-notice" role="status" data-testid="topology-layout-unavailable">
                {t("common.topology.layout.unavailable")}
              </p>
            )}
            {layoutStale && (
              <p className="topo-layout-notice" role="status" data-testid="topology-layout-notice">
                {t("common.topology.layout.stale")}
                {/* 조치할 수 있는 사람에게만 재저장을 안내합니다. */}
                {canEdit ? ` ${t("common.topology.layout.staleEditable")}` : ""}
              </p>
            )}
            <SectionView
              section={q.data?.graph}
              loading={q.isLoading}
              emptyTitle="통신 데이터가 없습니다"
              emptyDetail="선택한 범위에 관측된 Pod 간 통신이 없습니다."
            >
              {(g) => {
                const internal = g.nodes.filter((n) => !n.external);
                const foldedPods = internal.reduce((s, n) => s + (n.podCount ?? 1), 0);
                /* 닿는 선이 하나도 없는 워크로드는 기본으로 접습니다 — lnode에서 66개였고
                   그대로 그리면 떨어진 점들이 화면을 덮습니다. 빼는 것이 아니라 접는
                   것이라 수를 표시하고 펼 수 있게 둡니다. (ADR 0037 결정 9) */
                const isolated = g.nodes.filter((n) => n.isolated);
                const shownNodes = showIsolated ? g.nodes : g.nodes.filter((n) => !n.isolated);
                const hasStructure = (g.sources ?? []).includes("structure");
                return (
                <>
                  <TopologyGraph
                    nodes={shownNodes}
                    edges={g.edges}
                    savedPositions={q.data?.layout?.positions ?? null}
                    selectedEdgeId={selectedEdgeId}
                    editMode={editMode}
                    editSession={editSession}
                    onPositionsChange={(positions) => {
                      pendingPositions.current = positions;
                    }}
                    onSelectEdge={(id) =>
                      setParams(
                        (prev) => {
                          const p = new URLSearchParams(prev);
                          p.set("edge", id);
                          return p;
                        },
                        { replace: true },
                      )
                    }
                    onSelectNode={(n) => navigate(refPath(n.ref, search))}
                  />
                  <ul className="ds-topology__legend" style={{ marginTop: "var(--space-4)" }}>
                    <li>
                      워크로드 노드 {num(internal.length)}개 · Pod {num(foldedPods)}개를 접어서 표시
                    </li>
                    {/* 두 종류의 선이 뜻하는 바가 다릅니다. 이 구분을 지우면 ADR 0037을 되돌리는 것입니다. */}
                    {hasStructure ? (
                      <li data-testid="topology-origin-legend">
                        <i className="ds-topology__legend-dash" /> 도달 가능(구조) — Kubernetes가 연결해 둔 경로
                        <span className="muted"> · </span>
                        <i style={{ ["--_c" as string]: "var(--color-border-strong)" }} /> 실제 호출(트레이스)
                      </li>
                    ) : null}
                    {/* 진입점이 0건이면 밖에서 들어오는 파선이 하나도 생기지 않습니다. 워크로드 노드는 진입점보다
                        먼저 만들어지므로 `sources`에는 여전히 structure가 실리고, 그림만 보면 "구조는 섰는데
                        진입점이 없다"와 "구조가 잘 돌고 있다"가 구분되지 않습니다. 그 사유를 여기서 말합니다.
                        "구조가 아예 없다"는 `sources`에 structure가 빠지는 것으로 이미 표현되므로 다시 말하지
                        않습니다. 이것은 상태가 아니라 **구성 사실**이라 상태 색을 쓰지 않습니다. (ADR 0040 · #125) */}
                    {/* 성립하지 않은 진입점이 있으면 그 수를 말합니다. 선은 그대로 그려지므로
                        (ADR 0044 결정 6) 그림만 보면 "도달 가능"과 구분되지 않습니다 — 진입점
                        0건 안내와 같은 자리에서 그 사실을 말합니다. `unknown`은 미수용으로
                        세지 않습니다: 원천이 없는 것과 거절된 것은 다른 사실입니다. */}
                    {hasStructure && g.entryPoints && g.entryPoints.unaccepted > 0 ? (
                      <li data-testid="topology-entry-unaccepted">
                        {t("common.topology.entryAdmission.unaccepted", { count: g.entryPoints.unaccepted })}
                      </li>
                    ) : null}
                    {hasStructure && g.entryPoints && g.entryPoints.count === 0 ? (
                      <li data-testid="topology-entry-points">
                        구조는 섰지만 외부 진입점이 0건입니다 — 밖에서 들어오는 선이 그려지지 않습니다
                        <span className="muted">
                          {g.entryPoints.kinds.length > 0
                            ? ` · 이 배포가 색인하는 진입점 종류는 ${g.entryPoints.kinds.join(" · ")}이고, 이 범위 안에 그 객체가 하나도 없습니다`
                            : " · 이 배포는 진입점 종류(Ingress · HTTPRoute · Route)를 하나도 색인하지 않습니다 — 진입점이 없다는 뜻이 아닙니다"}
                        </span>
                      </li>
                    ) : null}
                    {/* 트레이스 기반 그래프(ADR 0033)는 계측 범위만큼만 보입니다 — 빈 곳을 "통신 없음"으로
                        읽지 않게 밝힙니다. 구조 그래프가 함께 있으면 무엇이 덜 보이는지가 달라집니다. */}
                    {hasStructure ? (
                      <li data-testid="topology-coverage">
                        워크로드 사이의 호출은 트레이스로 계측된 구간만 보입니다 — 선이 없다고 통신이 없는 것은
                        아닙니다
                        {/* 얼마나 안 보이는지를 수로 말합니다. 분모는 바로 위 "워크로드 노드 N개"와 같은
                            모집단이고 서버가 같은 규칙으로 셉니다 — 두 수가 어긋나면 어느 쪽도 믿을 수
                            없습니다. 구조가 없어 분모를 모르면 서버가 필드를 내지 않고, 그때 이 문장은
                            수 없이 그대로입니다("계측 0%"라는 거짓을 그리지 않습니다). 실측 어댑터가
                            상한에 걸려 버린 구간은 어떤 수로도 드러나지 않으므로 비율이 아니라 하한으로
                            말합니다. (ADR 0039 결정 4·6) */}
                        {g.coverage ? (
                          <>
                            {" · "}실측 호출이 닿은 워크로드 {num(g.coverage.instrumented)}개 / {num(g.coverage.workloads)}개
                            <span className="muted"> (하한 — 실측 상한에 걸려 빠진 구간은 세지 않습니다)</span>
                          </>
                        ) : null}
                      </li>
                    ) : g.source === "traces" ? (
                      <li data-testid="topology-coverage">
                        트레이스로 계측된 서비스 기준 · 계측되지 않은 워크로드끼리의 호출은 보이지 않습니다
                      </li>
                    ) : null}
                    {isolated.length > 0 ? (
                      <li data-testid="topology-isolated">
                        연결된 선이 없는 워크로드 {num(isolated.length)}개
                        <button
                          type="button"
                          className="linkish"
                          style={{ marginLeft: "var(--space-3)" }}
                          onClick={() => setShowIsolated((v) => !v)}
                        >
                          {showIsolated ? "접기" : "펼치기"}
                        </button>
                      </li>
                    ) : null}
                    {(g.omitted?.nodes ?? 0) > 0 || (g.omitted?.edges ?? 0) > 0 ? (
                      <li data-testid="topology-omitted">
                        상한을 넘어 생략: 노드 {num(g.omitted?.nodes ?? 0)}개 · 선 {num(g.omitted?.edges ?? 0)}개
                      </li>
                    ) : null}
                    {/* NetworkPolicy는 **선이 되지 않습니다** — 허용 정책은 호출이 아닙니다(ADR 0042 결정 1).
                        수의 주어는 namespace이고 노드는 워크로드라, 노드 카드가 아니라 범례에서 말합니다(결정 2·3).
                        상한 안내와 한 문장에 섞지 않습니다 — 정책 수는 노드 상한과 무관한 파티션의 사실입니다(결정 6).
                        존재는 상태가 아니므로 상태 색을 쓰지 않습니다(결정 7). */}
                    {g.networkPolicies ? (
                      <li data-testid="topology-network-policies">
                        NetworkPolicy가 있는 namespace {num(g.networkPolicies.withPolicy)}개 / {num(g.networkPolicies.namespaces)}개 ·
                        정책 {num(g.networkPolicies.policies)}개
                        <span className="muted">
                          {" "}
                          — 정책이 있다는 사실일 뿐 트래픽이 막혀 있다는 뜻이 아닙니다. 어떤 워크로드가 정책 대상인지는 이 화면이
                          알지 못합니다
                        </span>
                      </li>
                    ) : g.networkPoliciesNotIndexed ? (
                      /* "정책 0개"와 같은 화면으로 처리하지 않습니다. 이 배포는 세지 못한 것이지
                         정책이 없는 것이 아닙니다. (ADR 0042 결정 4) */
                      <li data-testid="topology-network-policies">
                        이 배포는 NetworkPolicy를 색인하지 않습니다
                        <span className="muted"> — 정책이 하나도 없다는 뜻이 아닙니다</span>
                      </li>
                    ) : null}
                    <li>
                      <i style={{ ["--_c" as string]: "var(--color-status-serious, var(--status-critical))" }} /> 선택한 경로
                    </li>
                    <li>
                      <i style={{ ["--_c" as string]: "var(--color-border-strong)" }} /> 그 외 경로
                    </li>
                    <li>
                      선 두께 = 트래픽 양 · 프로토콜은 노드 카드의 In/Out 요약으로 · 방향·Route·Count 상세는 아래 표에서 · 상태는 노드
                      카드 테두리 색으로 표시
                    </li>
                    {/* 구조 엣지는 두께가 1px 고정입니다 — 도달 가능성은 양이 아닙니다. 경로는 색이 아니라 텍스트로 말합니다. */}
                    {hasStructure ? (
                      <li data-testid="topology-paths-legend">
                        도달 가능(구조) 선을 고르면 호스트·경로·서비스 포트·엔드포인트 포트·Ready를 아래 표에서 텍스트로 봅니다
                      </li>
                    ) : null}
                  </ul>
                </>
                );
              }}
            </SectionView>
          </Panel>

          <div className="grid grid--split">
            <Panel
              title={
                edge ? (
                  <span className="row">
                    <span className="ds-ident">{nodeName(edge.from)}</span>
                    <span className="muted" aria-hidden="true">
                      →
                    </span>
                    <span className="ds-ident">{nodeName(edge.to)}</span>
                    <StatusBadge severity={edge.severity} small />
                  </span>
                ) : (
                  "방향 상세"
                )
              }
              subtitle={
                edge
                  ? structural
                    ? `도달 가능(구조) · 경로 ${num(edge.paths?.length ?? 0)}개 · Kubernetes가 연결해 둔 경로이며 호출 기록이 아닙니다`
                    : `${RANGE_LABEL[range]} · 누적 ${num(edge.totalCount)}건 · 에러율 ${(edge.errorRate * 100).toFixed(1)}% · ${
                        reverse ? `반대 방향(${nodeName(edge.to)} → ${nodeName(edge.from)})은 별도 선입니다` : "이 방향으로만 관측됩니다"
                      }`
                  : undefined
              }
              actions={
                edge &&
                !structural && (
                  <button
                    type="button"
                    className="ds-icon-button"
                    aria-pressed={mode === "chart"}
                    aria-label={mode === "chart" ? "표로 보기" : "시계열 차트로 보기"}
                    title={mode === "chart" ? "표로 보기" : "시계열 차트로 보기"}
                    onClick={() => setMode((m) => (m === "chart" ? "table" : "chart"))}
                  >
                    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 13.5V2.5" />
                      <path d="M2 13.5h12" />
                      <path d="M4 10.5l3-3 2.5 2.5L14 4.5" />
                    </svg>
                  </button>
                )
              }
              flush={structural || mode === "table"}
            >
              {!edge ? (
                <div className="state">
                  <span className="state__glyph" aria-hidden="true">
                    →
                  </span>
                  <span className="state__title">선을 선택하세요</span>
                </div>
              ) : structural ? (
                <StructuralPaths edge={edge} />
              ) : mode === "table" ? (
                <div className="panel__scroll">
                  <table className="ds-data-table ds-data-table--compact">
                    <thead>
                      <tr>
                        <th style={{ width: 110 }}>통신 종류</th>
                        <th>Route 종류 (혹은 API)</th>
                        <th className="ds-num" style={{ width: 140 }}>
                          누적 Count
                        </th>
                        <th className="ds-num" style={{ width: 100 }}>
                          에러
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {edge.routes.map((r) => {
                        const routeKey = `${r.protocol}-${r.route}`;
                        const open = openRoute === routeKey;
                        return (
                          <Fragment key={routeKey}>
                            <tr>
                              <td>{r.protocol}</td>
                              <td className="ds-ident">
                                <button
                                  type="button"
                                  className="linkish topo-route-toggle"
                                  aria-expanded={open}
                                  onClick={() => setOpenRoute(open ? null : routeKey)}
                                  title="이 경로의 최근 실제 로그 보기"
                                >
                                  {r.route}
                                </button>
                              </td>
                              <td className="ds-num">{num(r.count)}</td>
                              <td className="ds-num">{num(r.errorCount)}</td>
                            </tr>
                            {open && (
                              <tr className="topo-route-sample">
                                <td colSpan={4}>
                                  <RouteLogs
                                    clusterId={clusterId}
                                    fromNode={graph?.nodes.find((nn) => nn.id === edge.from)}
                                    route={r.route}
                                    range={range}
                                  />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                      <tr>
                        <td colSpan={2} style={{ font: "var(--type-label)", color: "var(--color-text-secondary)" }}>
                          합계
                        </td>
                        <td className="ds-num" style={{ font: "var(--type-body-strong)" }}>
                          {num(edge.totalCount)}
                        </td>
                        <td className="ds-num">{num(edge.routes.reduce((s, r) => s + r.errorCount, 0))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <SectionView section={series.data?.series} loading={series.isLoading} emptyTitle="시계열이 없습니다">
                  {(s) => (
                    <>
                      <LineChart
                        series={s}
                        stepSeconds={series.data!.range.stepSeconds}
                        ariaLabel={`${nodeName(edge.from)}에서 ${nodeName(edge.to)}로의 Route별 요청 수 시계열`}
                      />
                      <div className="muted" style={{ font: "var(--type-meta)", marginTop: "var(--space-3)" }}>
                        {RANGE_LABEL[range]} · step{" "}
                        {series.data!.range.stepSeconds >= 3600
                          ? `${series.data!.range.stepSeconds / 3600}시간`
                          : `${series.data!.range.stepSeconds / 60}분`}{" "}
                        · 상위 3개 Route + 나머지는 “기타”로 접음
                      </div>
                    </>
                  )}
                </SectionView>
              )}
            </Panel>

            <Panel title="비정상 Pods" subtitle="이름을 클릭하면 Pod 상세로 이동합니다" section={q.data?.pods} referenceIso={q.data?.generatedAt} flush>
              <SectionView section={q.data?.pods} loading={q.isLoading} emptyTitle="비정상 Pod가 없습니다">
                {(p) =>
                  p.unhealthyList.length === 0 ? (
                    <div className="state">
                      <span className="state__glyph" aria-hidden="true">
                        ✓
                      </span>
                      <span className="state__title">비정상 Pod가 없습니다</span>
                    </div>
                  ) : (
                    <table className="ds-data-table ds-data-table--compact">
                      <thead>
                        <tr>
                          <th>Pod</th>
                          <th>상태</th>
                          <th className="ds-num" style={{ width: 84 }}>
                            Restarts
                          </th>
                          <th style={{ width: 80 }}>지속</th>
                          <th style={{ width: 60 }}>로그</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.unhealthyList.map((u) => (
                          <tr key={u.name}>
                            <td className="ds-ident">
                              <Link to={refPath(u.ref, search)}>{u.name}</Link>
                            </td>
                            <td>
                              <StatusBadge severity={u.severity} label={u.reason} small />
                            </td>
                            <td className="ds-num">{u.restarts}</td>
                            <td className="num muted">{duration(u.forSeconds)}</td>
                            <td>
                              <LogsLink resourceRef={u.ref} search={search}>열기</LogsLink>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                }
              </SectionView>
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
